import { useState, useEffect, useCallback } from 'react';
import { getSubscriptionStatus } from '../lib/api';

/**
 * Hook to check subscription status and feature access
 */
export const useSubscription = () => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSubscription = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getSubscriptionStatus();
            setSubscription(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch subscription:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubscription();
    }, [fetchSubscription]);

    /**
     * Check if user has access to a specific feature
     * @param {string} feature - 'ai' | 'autofix' | 'basic'
     */
    const hasFeatureAccess = useCallback((feature) => {
        if (!subscription) return false;

        const status = subscription.status;
        if (status !== 'active' && status !== 'trial') return false;

        const features = subscription.features || {};

        switch (feature) {
            case 'ai':
                return features.includes_ai === true;
            case 'autofix':
                return features.includes_autofix === true;
            case 'basic':
                return true; // All subscribed users have basic features
            default:
                return false;
        }
    }, [subscription]);

    /**
     * Get the current subscription tier
     */
    const getTier = useCallback(() => {
        return subscription?.tier || null;
    }, [subscription]);

    /**
     * Check if user is subscribed (active or trial)
     */
    const isSubscribed = useCallback(() => {
        if (!subscription) return false;
        return subscription.status === 'active' || subscription.status === 'trial';
    }, [subscription]);

    return {
        subscription,
        loading,
        error,
        hasFeatureAccess,
        getTier,
        isSubscribed,
        refreshSubscription: fetchSubscription
    };
};

export default useSubscription;
